import { Close } from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  forwardRef,
  PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'

const Modal = forwardRef<ModalRef, ModalProps>((props, ref) => {
  const {
    actions,
    closesWithConfirm = false,
    onClose,
    children,
    ...passingProps
  } = props

  const { t } = useTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const contentRef = useRef<HTMLElement>(null)

  useImperativeHandle(ref, () => ({
    toTop: () => {
      contentRef.current?.scrollTo(0, 0)
    },
  }))

  const handleClose = useCallback(() => {
    if (closesWithConfirm) {
      if (window.confirm(t('Sure to leave? Your work will not be saved.'))) {
        onClose?.()
      }

      return
    }

    onClose?.()
  }, [closesWithConfirm, onClose, t])

  return (
    <Dialog
      container={document.getElementById('root')?.firstElementChild}
      onClose={handleClose}
      {...(downSm && {
        fullScreen: true,
        slots: { transition: Slide },
        slotProps: { transition: { direction: 'up' } },
      })}
      {...passingProps}
    >
      <Box textAlign="end" px={2.5} py={1.25}>
        <IconButton onClick={handleClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent dividers ref={contentRef}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions>
          {actions.map((buttonProps, index, array) => (
            <Button
              key={JSON.stringify(buttonProps)}
              {...(array.length > 1 && !index && { variant: 'outlined' })}
              {...buttonProps}
            />
          ))}
        </DialogActions>
      )}
    </Dialog>
  )
})

type ModalProps = PropsWithChildren &
  Pick<DialogProps, 'open' | 'maxWidth' | 'fullWidth'> & {
    actions?: [ButtonProps, ButtonProps?]
    closesWithConfirm?: boolean
    onClose?: VoidFunction
  }

type ModalRef = {
  toTop: VoidFunction
}

export { Modal }
export type { ModalProps, ModalRef }
